import { FC, memo, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useResponsive } from '../../../../hooks';
import { Button, message, Text, SVG } from '../../components-v4';
import { setShowCallCard } from '../../request-single-page-slice';
import { ReactComponent as PhoneIcon } from '../../assets/phone.svg';
import { ReactComponent as CopyIcon } from '../../assets/clone.svg';
import { formatStrNumberWithCode } from '../../utils';
import {
  GradientWrapper,
  Content,
  PhonesRow,
  PhoneItem,
  NumberChip,
} from './phone.styled';
import { useSelector } from 'react-redux';
import { getOnLoadPublicRequestState } from '../../selectors';
import { MIN_SKELETON_DURATION } from '../../constants';
import { BannerSkeleton } from '../skeletons';

type Props = {
  phoneNumbers: string[];
  inHeader: boolean;
};

export const Phone: FC<Props> = memo(({ phoneNumbers, inHeader }) => {
  const { viewport } = useResponsive();
  const dispatch = useAppDispatch();

  const onLoadRequest = useSelector(getOnLoadPublicRequestState);
  const [showSkeleton, setShowSkeleton] = useState<boolean>(onLoadRequest);
  const loadStartRef = useRef<number | null>(onLoadRequest ? Date.now() : null);

  useEffect(() => {
    if (onLoadRequest) {
      loadStartRef.current = Date.now();
      setShowSkeleton(true);
    } else if (loadStartRef.current !== null) {
      const elapsed = Date.now() - loadStartRef.current;
      const remaining = MIN_SKELETON_DURATION - elapsed;

      if (remaining > 0) {
        const timer = setTimeout(() => setShowSkeleton(false), remaining);

        return () => clearTimeout(timer);
      }

      setShowSkeleton(false);
    }
  }, [onLoadRequest]);

  const handleShowCallCard = () => {
    dispatch(setShowCallCard(true));
  };

  const handleCopy = (phoneNumber: string) => {
    navigator.clipboard
      .writeText(phoneNumber)
      .then(() => {
        message({
          type: 'success',
          text: 'Номер скопирован',
          style: { width: 'fit-content' },
        });
      })
      .catch(() => {
        message({
          type: 'error',
          text: 'Упс... Что-то пошло не так, повторите попытку',
        });
      });
  };

  // Телефоны диспетчерской не найдены - элемент не отображаем,
  // место под него не резервируем
  if (!phoneNumbers.length) {
    return null;
  }

  if (inHeader) {
    return (
      <Button
        mode="additional"
        variant="main"
        size="m"
        iconButton={<PhoneIcon />}
        onClick={handleShowCallCard}
      />
    );
  }

  if (showSkeleton) {
    return (
      <div>
        <BannerSkeleton />
      </div>
    );
  }

  return (
    <GradientWrapper isMobile={viewport.mobile}>
      <Content>
        <Text variant={viewport.mobile ? 'body-14' : 'body-16'} color="white">
          Позвонить в диспетчерскую службу
        </Text>
        <PhonesRow>
          {phoneNumbers.map((phoneNumber) => (
            <PhoneItem
              key={phoneNumber}
              onClick={() => handleCopy(phoneNumber)}
            >
              <NumberChip>
                <Text
                  variant={viewport.mobile ? 'body-14' : 'body-16'}
                  color="white"
                >
                  {formatStrNumberWithCode(phoneNumber)}
                </Text>
              <SVG
                src={CopyIcon}
                props={{
                  color: '#ffffff',
                  subColor: '#ffffff',
                  hoverColor: '#ffffff',
                  subHoverColor: '#ffffff',
                  size: '24px',
                }}
                complex={true}
              />
              </NumberChip>
            </PhoneItem>
          ))}
        </PhonesRow>
      </Content>
    </GradientWrapper>
  );
});
